import { nodeLabels, nodeWidth, nodeDetail } from './node-display.mjs'

export function rectConnection(a, b, gap = 3) {
  const dx = b.x - a.x,
    dy = b.y - a.y
  const distance = Math.hypot(dx, dy) || 1,
    ux = dx / distance,
    uy = dy / distance
  const boundary = (node) =>
    Math.min(ux ? node.width / 2 / Math.abs(ux) : Infinity, uy ? node.height / 2 / Math.abs(uy) : Infinity)
  const start = boundary(a) + gap,
    end = boundary(b) + gap
  const x = b.x - ux * end,
    y = b.y - uy * end
  return {
    path: `M${a.x + ux * start} ${a.y + uy * start} L${x} ${y}`,
    arrow: `${x - ux * 5 - uy * 3},${y - uy * 5 + ux * 3} ${x},${y} ${x - ux * 5 + uy * 3},${y - uy * 5 - ux * 3}`,
  }
}

// Lay out actual matches with the same labels and rectangular nodes as the main graph.
export function miniature(document, match, schemas, labels = nodeLabels(document, schemas)) {
  const records = new Map([...document.paragraphs, ...document.units].map((node) => [node.id, node]))
  const nodes = match.nodeIds
    .map((id) => records.get(id))
    .filter(Boolean)
    .map((node) => {
      const label = labels.get(node.id) || node.id
      return {
        ...node,
        label,
        detail: nodeDetail(node, document),
        width: nodeWidth(label, !!node.paragraphId),
        height: node.paragraphId ? 29 : 35,
      }
    })
  const ids = new Set(nodes.map((node) => node.id))
  const edges = document.edges.filter(
    (edge) => match.edgeIds.includes(edge.id) && ids.has(edge.source) && ids.has(edge.target),
  )
  const ranks = new Map(nodes.map((node) => [node.id, 0]))
  let changed = false
  for (let i = 0; i < nodes.length; i++) {
    changed = false
    for (const edge of edges)
      if (ranks.get(edge.target) <= ranks.get(edge.source)) {
        ranks.set(edge.target, ranks.get(edge.source) + 1)
        changed = true
      }
    if (!changed) break
  }
  const levels = [...new Set(ranks.values())].sort((a, b) => a - b)
  const rows = levels.map((level) => nodes.filter((node) => ranks.get(node.id) === level))
  const rowWidth = (row) => row.reduce((sum, node) => sum + node.width, 0) + Math.max(0, row.length - 1) * 30
  let width = Math.max(200, ...rows.map((row) => rowWidth(row) + 32))
  let height = Math.max(150, levels.length * 72 + 24)
  let placed
  if (changed) {
    const maxWidth = Math.max(...nodes.map((node) => node.width))
    const radius = (Math.hypot(maxWidth, 35) + 30) / (2 * Math.sin(Math.PI / Math.max(2, nodes.length)))
    width = radius * 2 + maxWidth + 32
    height = radius * 2 + 67
    placed = nodes.map((node, i) => {
      const angle = (2 * Math.PI * i) / nodes.length - Math.PI / 2
      return { ...node, x: width / 2 + radius * Math.cos(angle), y: height / 2 + radius * Math.sin(angle) }
    })
  } else {
    placed = rows.flatMap((row, i) => {
      let x = (width - rowWidth(row)) / 2
      return row.map((node) => {
        const placed = { ...node, x: x + node.width / 2, y: 34 + i * 72 }
        x += node.width + 30
        return placed
      })
    })
  }
  const positions = new Map(placed.map((node) => [node.id, node]))
  return {
    width,
    height,
    nodes: placed,
    edges: edges.map((edge) => ({
      ...edge,
      ...rectConnection(positions.get(edge.source), positions.get(edge.target)),
    })),
  }
}
